import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { Capacitor } from '@capacitor/core';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { invalidate } from './api/apiCache';
import { ThemeProvider } from './context/ThemeContext';
import { PaywallProvider } from './context/PaywallContext';
import PrivateRoute from './components/PrivateRoute';
import BottomNav from './components/BottomNav';
import BiometricLockGate from './components/BiometricLockGate';
import IdleLogoutGate from './components/IdleLogoutGate';
import FundingHandoffWatcher from './components/FundingHandoffWatcher';
import PushTapRouter from './components/PushTapRouter';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import About from './pages/About';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import AutomationDisclosure from './pages/AutomationDisclosure';
import Help from './pages/Help';
import BlogIndex from './pages/BlogIndex';
import BlogPost from './pages/BlogPost';
import Pricing from './pages/Pricing';
import Landing from './pages/Landing';
import './App.css';

// Authenticated-app pages are lazy-loaded so an anonymous visitor hitting the public
// Landing page doesn't download the entire authenticated app (Automations, Settings,
// Portfolio, recharts, etc.) in the same bundle as the marketing site. Public/auth pages
// above stay eager — they're what an anonymous visitor needs immediately.
// The chat widget renders only for signed-in users, and it carries the Markdown renderer
// (~30% of the old main chunk), so it loads on its own. initialBundle.test.js keeps it that way.
const ChatWidget = lazy(() => import('./components/ChatWidget'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Insights = lazy(() => import('./pages/Insights'));
const Goals = lazy(() => import('./pages/Goals'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Expenses = lazy(() => import('./pages/Expenses'));
const Budgets = lazy(() => import('./pages/Budgets'));
const Automations = lazy(() => import('./pages/Automations'));
const Watchlist = lazy(() => import('./pages/Watchlist'));
const Settings = lazy(() => import('./pages/Settings'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const TickerProfile = lazy(() => import('./pages/TickerProfile'));
const ChatPage = lazy(() => import('./pages/ChatPage'));

function RouteFallback() {
  return <div role="status" aria-live="polite" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function BillingSuccessHandler() {
  const { search } = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get('upgrade') === 'success') {
      invalidate('profile');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [search]);
  return null;
}

// capacitor.config.json sets launchAutoHide: false, so the native splash screen
// stays up forever until this fires — dynamic import keeps @capacitor/splash-screen
// out of the web bundle, where it's never needed.
function NativeSplashHider() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    import('@capacitor/splash-screen').then(({ SplashScreen }) => SplashScreen.hide()).catch(() => {});
  }, []);
  return null;
}

// Renders the chat widget + mobile bottom nav only when authenticated — must be inside AuthProvider.
// Both are suppressed on /chat (the page IS the chat UI; bottom nav would overlap the input).
function AuthShell() {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  if (!isAuthenticated || pathname === '/chat') return null;
  return (
    <>
      <BottomNav />
      {/* A floating button: nothing to hold space for while its chunk loads. */}
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
    </>
  );
}

// "Fund account" (v1.0.2 §3.4): looks for the deposit when the user returns to the app, on any
// signed-in page (chat included, unlike AuthShell).
function FundingShell() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <FundingHandoffWatcher /> : null;
}

// `/` serves the marketing landing page to logged-out visitors and the
// Dashboard to authenticated users (previously bounced logged-out users to /login).
function HomeRoute() {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return <Dashboard />;
  // Native has no non-account-based functionality to show pre-login — every feature
  // (insights, portfolio, chat, etc.) requires a user's own linked bank/brokerage data —
  // so skip the web-only marketing landing page straight to sign-in. Flagged as Guideline
  // 5.1.1(v) in the 2026-09-14 App Store review; appealed with this reasoning rather than
  // reverted (see project-memory/context/decisions/infra-and-tooling.md).
  if (Capacitor.isNativePlatform()) return <Navigate to="/login" replace />;
  return <Landing />;
}

export default function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
      {/* Inside the router because the paywall links to /terms and /privacy (Guideline
          3.1.2(c)) — a <Link> rendered outside a router throws. */}
      <PaywallProvider>
        <ScrollToTop />
        <BillingSuccessHandler />
        <NativeSplashHider />
        <PushTapRouter />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<HomeRoute />} />
            <Route path="/insights" element={<PrivateRoute><Insights /></PrivateRoute>} />
            <Route path="/goals" element={<PrivateRoute><Goals /></PrivateRoute>} />
            <Route path="/portfolio" element={<PrivateRoute><Portfolio /></PrivateRoute>} />
            <Route path="/expenses" element={<PrivateRoute><Expenses /></PrivateRoute>} />
            <Route path="/budgets" element={<PrivateRoute><Budgets /></PrivateRoute>} />
            <Route path="/automations" element={<PrivateRoute><Automations /></PrivateRoute>} />
            <Route path="/watchlist" element={<PrivateRoute><Watchlist /></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
            <Route path="/admin/users" element={<PrivateRoute><AdminUsers /></PrivateRoute>} />
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/automation-disclosure" element={<AutomationDisclosure />} />
            <Route path="/help" element={<Help />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/ticker/crypto/:base/:quote" element={<PrivateRoute><TickerProfile /></PrivateRoute>} />
            <Route path="/ticker/crypto/:base" element={<PrivateRoute><TickerProfile /></PrivateRoute>} />
            <Route path="/ticker/:symbol" element={<PrivateRoute><TickerProfile /></PrivateRoute>} />
            <Route path="/chat" element={<PrivateRoute><ChatPage /></PrivateRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <AuthShell />
        <FundingShell />
        <BiometricLockGate />
        <IdleLogoutGate />
      </PaywallProvider>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}
